<template>
  <v-dialog
    :model-value="modelValue"
    max-width="480"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card
      v-if="slot"
      class="slot-dialog"
    >
      <v-card-title class="pa-0">
        <v-toolbar
          color="primary"
          flat
        >
          <v-toolbar-title class="text-white">{{
            t('content-creator.post-details')
          }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              :aria-label="t('marketing-agent.dialog.close-preview')"
              @click="emit('update:modelValue', false)"
            >
              <v-icon
                size="large"
                class="text-white"
              >
                mdi-close
              </v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>

      <div class="slot-dialog__body">
        <PreviewStep
          class="slot-dialog__preview"
          hide-offices
          hide-submit-button
          :offices="[]"
          submit-text=""
          :content="previewContent"
          :custom-statistics="scheduleStatistics"
        >
          <template #media="{ src }">
            <div class="slot-dialog__media">
              <v-img
                :src="src"
                :alt="previewContent.text"
                cover
                max-height="360"
              />

              <!--
                // For the MVP we don't need to show the user multiple text choices, so we hide the navigation buttons for now.
                <v-btn
                class="slot-dialog__nav slot-dialog__nav&#45;&#45;prev"
                icon="mdi-chevron-left"
                variant="outlined"
                size="small"
                :disabled="textSlides.length < 2"
                @click="stepChoice(-1)"
              />
              <v-btn
                class="slot-dialog__nav slot-dialog__nav&#45;&#45;next"
                icon="mdi-chevron-right"
                variant="outlined"
                size="small"
                :disabled="textSlides.length < 2"
                @click="stepChoice(1)"
              />-->
            </div>
          </template>
        </PreviewStep>
      </div>

      <div
        v-if="!readOnly"
        class="slot-dialog__footer"
      >
        <div class="slot-dialog__actions">
          <v-btn
            v-if="failed"
            class="slot-dialog__pill"
            variant="flat"
            color="primary"
            prepend-icon="mdi-refresh"
            rounded="pill"
            :loading="busy === 'recreate'"
            :disabled="!!busy"
            @click="onRecreate"
          >
            {{ t('marketing-agent.dialog.retry') }}
          </v-btn>
          <v-btn
            class="slot-dialog__delete"
            icon="mdi-trash-can-outline"
            variant="outlined"
            color="error"
            :loading="busy === 'delete'"
            :disabled="!!busy"
            :aria-label="t('marketing-agent.dialog.delete')"
            @click="onDelete"
          />
          <v-btn
            class="slot-dialog__pill"
            variant="outlined"
            color="primary"
            prepend-icon="mdi-pencil"
            rounded="pill"
            :loading="busy === 'edit'"
            :disabled="!!busy || locked"
            @click.stop="openEditor"
          >
            {{ t('buttons.content-creator.scheduled-post.edit') }}
          </v-btn>
          <v-btn
            v-if="!isApproved"
            class="slot-dialog__pill"
            variant="outlined"
            color="primary"
            prepend-icon="mdi-calendar-month-outline"
            rounded="pill"
            :disabled="!!busy || locked"
            @click.stop="rescheduleOpen = true"
          >
            {{ t('buttons.content-creator.scheduled-post.reschedule') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <Teleport to="body">
    <EditContent
      v-if="editing && slotData"
      :content="{ text: editText }"
      :kind="editKind"
      :topic="false"
      force-editable
      @update="onEditSave"
      @close="editing = false"
    />
    <PlanOutline
      v-model="rescheduleOpen"
      :slot-data="slotData"
      :known-platforms="knownPlatforms"
      :saving="rescheduling"
      @save="onRescheduleSave"
    />
  </Teleport>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { MarketingPlanSlotStatus, MarketingPlanSlotTextStatus } from '@/generated/graphql'
import EditContent from '@/modules/contentCreator/creator/EditContent.vue'
import { TypeToKind } from '@/modules/contentCreator/creator/enums/KindType.js'
import PreviewStep from '@/modules/contentCreator/creator/SocialMedia/PreviewStep.vue'

import { slotTime } from './calendar/slotCard'
import PlanOutline from './PlanOutline.vue'

const $tracking = inject('tracking')

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  // Named slotData (not "slot") — a bare `slot` attr collides with Vue's reserved slot directive.
  slotData: {
    type: Object,
    default: null,
  },
  // Async actions from useMarketingPlan; each resolves once the cache is updated.
  recreate: {
    type: Function,
    required: true,
  },
  updateText: {
    type: Function,
    required: true,
  },
  remove: {
    type: Function,
    required: true,
  },
  approve: {
    type: Function,
    required: true,
  },
  updateSchedule: {
    type: Function,
    required: true,
  },
  knownPlatforms: {
    type: Array,
    default: () => [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const { t, locale } = useI18n()

const busy = ref(null)
const editing = ref(false)
const rescheduleOpen = ref(false)
const rescheduling = ref(false)
const choiceIndex = ref(0)

const slot = computed(() => props.slotData)
// Text the editor opens with. After a failed save it holds the user's edit.
const editText = ref('')

// The backend refuses edits while text is generating (the job would drop its
// result) and on approved slots (the scheduled post keeps its own copy).
const locked = computed(
  () =>
    slot.value?.textStatus === MarketingPlanSlotTextStatus.Generating ||
    slot.value?.status === MarketingPlanSlotStatus.Approved
)
const failed = computed(() => slot.value?.textStatus === MarketingPlanSlotTextStatus.Failed)
const editKind = computed(() => TypeToKind[slot.value?.platform] || 'facebookPost')

const isApproved = computed(() => slot.value?.status === MarketingPlanSlotStatus.Approved)

const textSlides = computed(() => {
  const choices = (slot.value?.textChoices || []).filter(Boolean)
  if (choices.length) return choices
  const text = slot.value?.generatedText || (failed.value ? t('marketing-agent.dialog.failed') : '')
  return text ? [text] : []
})

const previewContent = computed(() => ({
  topicName: slot.value?.topicName || '',
  image: slot.value?.imageUrl || '',
  text: textSlides.value[choiceIndex.value] || slot.value?.generatedText || '',
  subject: '',
}))

const scheduleStatistics = computed(() => {
  const scheduled = slot.value?.scheduledLocal
  if (!scheduled?.date) return []
  return [
    { icon: 'mdi-calendar-month-outline', text: formatScheduleDate(scheduled.date) },
    { icon: 'mdi-calendar-clock', text: slotTime(scheduled.time) },
  ]
})

watch(
  () => slot.value?.id,
  () => {
    choiceIndex.value = 0
  }
)

function formatScheduleDate(iso) {
  const [year, month, day] = iso.split('-').map(Number)
  return new Intl.DateTimeFormat(locale.value, {
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(year, month - 1, day))
}

/*function stepChoice(delta) {
  const count = textSlides.value.length
  if (count < 2) return
  choiceIndex.value = (choiceIndex.value + delta + count) % count
}*/

function openEditor() {
  editText.value = slot.value?.generatedText || ''
  editing.value = true
}

// EditContent closes itself after emitting the text. If saving fails, reopen it
// with that text so the user's edit is not lost. The parent shows the error.
async function onEditSave(text) {
  editing.value = false
  busy.value = 'edit'
  try {
    await props.updateText(slot.value.id, text)
    $tracking.heapEvent('marketing_plan_slot_text_edited', { platform: slot.value?.platform })
  } catch {
    editText.value = text
    editing.value = true
  } finally {
    busy.value = null
  }
}

// The slot goes back to GENERATING and the calendar polls for the result.
// The parent reports a failed request and shows the snackbar.
async function onRecreate() {
  busy.value = 'recreate'
  try {
    await props.recreate(slot.value.id)
    $tracking.heapEvent('marketing_plan_slot_regenerated', { platform: slot.value?.platform })
  } catch {
    // Reported by the parent.
  } finally {
    busy.value = null
  }
}

async function onRescheduleSave({ slotId, platform, date, time }) {
  rescheduling.value = true
  try {
    await props.updateSchedule(slotId, { platform, date, time })
    $tracking.heapEvent('marketing_plan_slot_rescheduled', { platform })
    rescheduleOpen.value = false
  } catch {
    // The parent already reported the error and showed the snackbar.
    // Keep the editor open so the user can try again.
  } finally {
    rescheduling.value = false
  }
}

async function onDelete() {
  const platform = slot.value?.platform
  busy.value = 'delete'
  try {
    const success = await props.remove(slot.value.id)
    if (success) {
      $tracking.heapEvent('marketing_plan_slot_deleted', { platform })
      emit('update:modelValue', false)
    }
  } catch (error) {
    // Parent already shows the error snackbar.
    // Keep the dialog open.
  } finally {
    busy.value = null
  }
}
</script>

<style scoped>
.slot-dialog {
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 48px);
  overflow: hidden;
}

.slot-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.slot-dialog__preview {
  height: 100%;
}

.slot-dialog__preview :deep(.preview-step) {
  height: 100%;
  overflow: hidden;
}

.slot-dialog__preview :deep(.preview-step-content--stacked) {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 20px 24px 12px;
}

.slot-dialog__preview :deep(.preview-step-content__summary) {
  flex: 0 0 auto;
}

.slot-dialog__preview :deep(.preview-step__preview) {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;
}

.slot-dialog__footer {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 20px 8px;
  border-top: 1px solid #ececec;
  background: #fff;
}

.slot-dialog__media {
  position: relative;
  width: 100%;
  min-width: 0;
}

.slot-dialog__media :deep(.v-img) {
  width: 100%;
}

.slot-dialog__nav {
  position: absolute;
  top: 50%;
  z-index: 1;
  background: #fff;
  transform: translateY(-50%);
}

.slot-dialog__nav--prev {
  left: 8px;
}

.slot-dialog__nav--next {
  right: 8px;
}

.slot-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.slot-dialog__pill {
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 1;
}
</style>
